import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, downloadAuthPdf } from "@/lib/api";
import { InternalLayout } from "@/components/InternalLayout";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeftIcon as ArrowLeft, BookOpenIcon as BookOpen, PlusIcon as Plus, WarningIcon as Warning, LockSimpleIcon as LockSimple, FilePdfIcon as FilePdf } from "@phosphor-icons/react";

const CAT_COLOR = {
  "Manutenzione": "bg-blue-100 text-blue-700 border-blue-200",
  "Incidente/Sinistro": "bg-red-100 text-red-700 border-red-200",
  "Lavaggio": "bg-cyan-100 text-cyan-700 border-cyan-200",
  "Rifornimento carburante": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Multa/Sanzione": "bg-orange-100 text-orange-700 border-orange-200",
};
const catColor = (c) => CAT_COLOR[c] || "bg-secondary text-foreground border-border";

export default function VeicoloLogbook() {
  const { targa } = useParams();
  const navigate = useNavigate();
  const [categorie, setCategorie] = useState([]);
  const [voci, setVoci] = useState(null);
  const [categoria, setCategoria] = useState("");
  const [nota, setNota] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/veicoli/${encodeURIComponent(targa)}/logbook`).then(({ data }) => setVoci(data.voci || [])).catch(() => setVoci([]));
  useEffect(() => {
    api.get("/veicoli/logbook/categorie").then(({ data }) => setCategorie(data.categorie || [])).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targa]);

  const openConfirm = () => {
    if (!categoria) { toast.error("Seleziona una categoria"); return; }
    if (!nota.trim()) { toast.error("Inserisci una nota"); return; }
    setConfirmOpen(true);
  };

  const doSave = async () => {
    // seconda conferma
    if (!window.confirm("Confermi DEFINITIVAMENTE l'inserimento? La voce sarà permanente e NON potrà più essere modificata o eliminata.")) return;
    setSaving(true);
    try {
      await api.post("/veicoli/logbook", { targa, categoria, nota });
      toast.success("Voce aggiunta al logbook");
      setConfirmOpen(false); setCategoria(""); setNota(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <InternalLayout>
      <div className="p-6 lg:p-10 max-w-4xl">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3" data-testid="logbook-back-btn"><ArrowLeft size={15} /> Indietro</button>
        <div className="flex items-start justify-between gap-3 mb-1">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><BookOpen size={20} weight="bold" className="text-primary" /></div>
            <div>
              <h1 className="font-heading text-2xl font-bold">Logbook mezzo</h1>
              <div className="font-heading font-bold tracking-wide bg-secondary px-2 py-0.5 rounded-sm border border-border inline-block mt-1 text-sm" data-testid="logbook-targa">{targa}</div>
            </div>
          </div>
          <Button variant="outline" className="rounded-sm gap-2" disabled={!voci || voci.length === 0} onClick={() => downloadAuthPdf(`/veicoli/${encodeURIComponent(targa)}/logbook/pdf`, `Logbook mezzo ${targa}.pdf`).catch(() => toast.error("Esportazione PDF non riuscita"))} data-testid="logbook-export-pdf-btn"><FilePdf size={16} /> Esporta PDF</Button>
        </div>
        <p className="text-xs text-muted-foreground mb-6 flex items-center gap-1"><LockSimple size={13} /> Registro cronologico immutabile: ogni voce è permanente e non modificabile.</p>

        {/* Form nuovo inserimento */}
        <Card className="rounded-sm border-border shadow-none p-5 mb-6" data-testid="logbook-form">
          <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><Plus size={16} weight="bold" /> Nuova voce</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Categoria</Label>
              <Select value={categoria} onValueChange={setCategoria}>
                <SelectTrigger className="mt-1 rounded-sm h-9" data-testid="logbook-categoria"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                <SelectContent>{categorie.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase tracking-wider">Nota</Label>
              <Textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={2} placeholder="Descrivi l'evento…" className="mt-1 rounded-sm text-sm" data-testid="logbook-nota" />
            </div>
          </div>
          <div className="flex justify-end mt-3">
            <Button onClick={openConfirm} className="rounded-sm gap-2" data-testid="logbook-add-btn"><Plus size={16} weight="bold" /> Aggiungi al logbook</Button>
          </div>
        </Card>

        {/* Timeline immutabile */}
        {voci === null ? <p className="text-muted-foreground">Caricamento…</p> : voci.length === 0 ? (
          <Card className="rounded-sm border-border shadow-none p-8 text-center" data-testid="logbook-empty"><BookOpen size={36} className="text-muted-foreground mx-auto mb-2" /><p className="text-sm text-muted-foreground">Nessuna voce registrata per questo mezzo.</p></Card>
        ) : (
          <div className="space-y-2" data-testid="logbook-list">
            {voci.map((v) => (
              <Card key={v.id} className="rounded-sm border-border shadow-none p-4" data-testid={`logbook-entry-${v.id}`}>
                <div className="flex items-center justify-between gap-3 mb-1">
                  <div className="flex items-center gap-2">
                    <Badge className={`rounded-sm ${catColor(v.categoria)}`}>{v.categoria}</Badge>
                    <span className="text-xs text-muted-foreground">{new Date(v.created_at).toLocaleString("it-IT")}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{v.created_by}</span>
                </div>
                <div className="text-sm whitespace-pre-wrap">{v.nota}</div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Prima conferma */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="logbook-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2 text-amber-700"><Warning size={18} weight="fill" /> Conferma inserimento</DialogTitle></DialogHeader>
          <div className="py-2 space-y-3 text-sm">
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-sm px-3 py-2">Attenzione: una volta salvata, la voce del logbook <b>NON potrà più essere modificata o eliminata</b>.</div>
            <div className="border border-border rounded-sm p-3">
              <div className="flex items-center gap-2 mb-1"><Badge className={`rounded-sm ${catColor(categoria)}`}>{categoria || "—"}</Badge></div>
              <div className="whitespace-pre-wrap">{nota}</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setConfirmOpen(false)} data-testid="logbook-confirm-cancel">Annulla</Button>
            <Button className="rounded-sm gap-2" onClick={doSave} disabled={saving} data-testid="logbook-confirm-ok"><LockSimple size={16} weight="bold" /> {saving ? "Salvataggio…" : "Conferma e salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </InternalLayout>
  );
}
